import { ActiveSelection, type Canvas } from "fabric";
import {
  ASSET_LAYER_TYPE,
  IMAGE_LAYER_TYPE,
  LINE_LAYER_TYPE,
  SHAPE_LAYER_TYPE,
  TEXT_LAYER_TYPE,
} from "../constants.ts";
import {
  fabricObjectToLayer,
  isAssetLayer,
  isImageLayer,
  isLineLayer,
  isShapeLayer,
  isTextLayer,
} from "../typeguard.ts";
import type {
  AlignmentAnchor,
  AssetLayer,
  BoundingRect,
  ImageLayer,
  Layer,
  LayerForType,
  LayerType,
  LineLayer,
  ShapeLayer,
  TextLayer,
} from "../types.ts";

// XXX: We define layer type specific overloads to have strict interfaces (shapeType is required for shape layers but not for non-shape layers...) and return correct types.
export function createLayer(params: {
  fabricCanvas: Canvas;
  layer: ShapeLayer;
  name: string;
  layerType: typeof SHAPE_LAYER_TYPE;
  positionIndex: number;
}): ShapeLayer;

export function createLayer(params: {
  fabricCanvas: Canvas;
  layer: ImageLayer;
  name: string;
  layerType: typeof IMAGE_LAYER_TYPE;
  positionIndex: number;
}): ImageLayer;

export function createLayer(params: {
  fabricCanvas: Canvas;
  layer: TextLayer;
  name: string;
  layerType: typeof TEXT_LAYER_TYPE;
  positionIndex: number;
}): TextLayer;

export function createLayer(params: {
  fabricCanvas: Canvas;
  layer: LineLayer;
  name: string;
  layerType: typeof LINE_LAYER_TYPE;
  positionIndex: number;
}): LineLayer;

export function createLayer(params: {
  fabricCanvas: Canvas;
  layer: AssetLayer;
  name: string;
  layerType: typeof ASSET_LAYER_TYPE;
  positionIndex: number;
}): AssetLayer;

export function createLayer({
  fabricCanvas,
  layer,
  name,
  layerType,
  positionIndex,
}: {
  fabricCanvas: Canvas;
  layer: Layer;
  name: string;
  layerType: LayerType;
  positionIndex: number;
}) {
  layer.id = `layer_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  layer.name = name;
  layer.layerType = layerType;
  layer.positionIndex = positionIndex;

  fabricCanvas.add(layer);
  fabricCanvas.setActiveObject(layer);
  fabricCanvas.renderAll();

  return layer;
}

export const setActiveLayers = ({
  fabricCanvas,
  activeLayerIds,
}: {
  fabricCanvas: Canvas;
  activeLayerIds: string[];
}) => {
  const layers = getLayers(fabricCanvas);
  if (activeLayerIds.length === 1) {
    const layer = layers.find((l) => l.id === activeLayerIds[0]);
    if (layer) {
      fabricCanvas.setActiveObject(layer);
      fabricCanvas.renderAll();
    }
  } else if (activeLayerIds.length > 1) {
    const layersToSelect = activeLayerIds
      .map((id) => layers.find((l) => l.id === id))
      .filter((layer): layer is Layer => layer !== undefined);
    if (layersToSelect.length > 0) {
      const activeSelection = new ActiveSelection(layersToSelect, {
        canvas: fabricCanvas,
      });
      fabricCanvas.setActiveObject(activeSelection);
      fabricCanvas.renderAll();
    }
  }
};

export const deleteLayer = (fabricCanvas: Canvas, layer: Layer) => {
  fabricCanvas.remove(layer);
  // XXX: We need to update the positionIndex of the layers after the layer removal to make sure the layers indexes remain consistent with array length/indexes.
  for (const l of getLayers(fabricCanvas)) {
    if (l.positionIndex > layer.positionIndex) l.positionIndex--;
  }
};

export const getLayers = (fabricCanvas: Canvas) =>
  fabricCanvas
    .getObjects()
    .map(fabricObjectToLayer)
    .filter((it) => it !== null)
    .sortDesc((it) => it.positionIndex);

export const getSingleAssetLayer = (fabricCanvas: Canvas) => {
  const assetLayers = getLayers(fabricCanvas).filter(
    (l) => l.layerType === ASSET_LAYER_TYPE,
  );
  if (assetLayers.length === 1) return assetLayers[0];
  return undefined;
};

export const getLayerTypes = (layers: Layer[]): LayerType[] =>
  Array.from(new Set(layers.map((it) => it.layerType)));

export const normalizeLayerAngle = (angle: number): number =>
  angle > 180 ? angle - 360 : angle;

export const getActiveLayers = ({
  fabricCanvas,
}: {
  fabricCanvas?: Canvas;
}): Layer[] => {
  const activeLayers: Layer[] = [];
  if (!fabricCanvas) return activeLayers;

  for (const object of fabricCanvas.getActiveObjects()) {
    const layer = fabricObjectToLayer(object);
    if (!layer) continue;
    activeLayers.push(layer);
  }

  return activeLayers;
};

export const getSpecificActiveLayers = <T extends LayerType>({
  fabricCanvas,
  layerType,
}: {
  fabricCanvas?: Canvas;
  layerType: T;
}): LayerForType<T>[] => {
  const activeLayers: LayerForType<T>[] = [];

  for (const layer of getActiveLayers({ fabricCanvas })) {
    switch (layerType) {
      case TEXT_LAYER_TYPE:
        if (isTextLayer(layer)) activeLayers.push(layer as LayerForType<T>);
        break;
      case IMAGE_LAYER_TYPE:
        if (isImageLayer(layer)) activeLayers.push(layer as LayerForType<T>);
        break;
      case SHAPE_LAYER_TYPE:
        if (isShapeLayer(layer)) activeLayers.push(layer as LayerForType<T>);
        break;
      case LINE_LAYER_TYPE:
        if (isLineLayer(layer)) activeLayers.push(layer as LayerForType<T>);
        break;
      case ASSET_LAYER_TYPE:
        if (isAssetLayer(layer)) activeLayers.push(layer as LayerForType<T>);
        break;
    }
  }

  return activeLayers;
};

export const deleteSelectedLayers = (fabricCanvas: Canvas) => {
  const activeObjects = fabricCanvas.getActiveObjects();
  if (activeObjects.length > 0) {
    fabricCanvas.remove(...activeObjects);
    fabricCanvas.discardActiveObject();
  }
};

export const computeLayersBoundingRect = (layers: Layer[]) => {
  const boundingRect = layers.reduce(
    (acc, layer) => ({
      ...acc,
      top: Math.min(acc.top, layer.getBoundingRect().top),
      left: Math.min(acc.left, layer.getBoundingRect().left),
      bottom: Math.max(
        acc.bottom,
        layer.getBoundingRect().top + layer.getBoundingRect().height,
      ),
      right: Math.max(
        acc.right,
        layer.getBoundingRect().left + layer.getBoundingRect().width,
      ),
    }),
    { top: Infinity, left: Infinity, bottom: -Infinity, right: -Infinity },
  );
  return {
    top: boundingRect.top,
    left: boundingRect.left,
    bottom: boundingRect.bottom,
    right: boundingRect.right,
  };
};

export const alignLayersRelativeToCanvas = (
  fabricCanvas: Canvas,
  layers: Layer[],
  alignmentPosition: AlignmentAnchor,
) => {
  const canvasWidth = fabricCanvas.getWidth();
  const canvasHeight = fabricCanvas.getHeight();
  applyLayerAlignment(
    fabricCanvas,
    layers,
    {
      top: 0,
      left: 0,
      bottom: canvasHeight,
      right: canvasWidth,
    },
    alignmentPosition,
  );
};

export const alignLayersRelativeToSelection = (
  fabricCanvas: Canvas,
  layers: Layer[],
  alignmentPosition: AlignmentAnchor,
) => {
  const layersBoundingRect = computeLayersBoundingRect(layers);
  applyLayerAlignment(
    fabricCanvas,
    layers,
    layersBoundingRect,
    alignmentPosition,
  );
};

export const scaleAndTranslateLayers = (
  fabricCanvas: Canvas,
  layers: Layer[],
  transformParams: {
    scale?: number;
    translateX?: number;
    translateY?: number;
  },
) => {
  if (layers.length === 0) return;

  for (const layer of layers) {
    layer.set({
      scaleX: layer.scaleX * (transformParams.scale ?? 1),
      scaleY: layer.scaleY * (transformParams.scale ?? 1),
      left:
        (transformParams.scale ?? 1) * layer.left +
        (transformParams.translateX ?? 0),
      top:
        (transformParams.scale ?? 1) * layer.top +
        (transformParams.translateY ?? 0),
    });

    layer.setCoords();
  }

  fabricCanvas.renderAll();
};

const applyLayerAlignment = (
  fabricCanvas: Canvas,
  layers: Layer[],
  referenceBoundingRect: BoundingRect,
  alignmentPosition: AlignmentAnchor,
) => {
  // XXX: When multiple layers are selected, `Fabric.js` groups them into an ActiveSelection object.
  // This can interfere with alignment positioning because:
  // 1. The positioning calculations might be relative to the group instead of individual layers
  // 2. The getBoundingRect() method returns different values for grouped vs individual layers
  // 3. The set() method behavior can be affected by the group context
  // To solve this, we temporarily ungroup the layers during alignment:
  // - Discard the ActiveSelection to work with individual layer coordinates
  // - Apply alignment calculations to each layer independently
  // - Recreate the ActiveSelection to preserve the user's selection state

  const activeObject = fabricCanvas.getActiveObject();
  const isActiveSelection = activeObject instanceof ActiveSelection;

  if (isActiveSelection && layers.length > 1) {
    fabricCanvas.discardActiveObject();
  }

  for (const layer of layers) {
    switch (alignmentPosition) {
      case "top":
        layer.set({
          top: referenceBoundingRect.top + layer.getBoundingRect().height / 2,
        });
        break;
      case "bottom":
        layer.set({
          top:
            referenceBoundingRect.bottom - layer.getBoundingRect().height / 2,
        });
        break;
      case "centerY":
        layer.set({
          top: (referenceBoundingRect.top + referenceBoundingRect.bottom) / 2,
        });
        break;
      case "left":
        layer.set({
          left: referenceBoundingRect.left + layer.getBoundingRect().width / 2,
        });
        break;
      case "right":
        layer.set({
          left: referenceBoundingRect.right - layer.getBoundingRect().width / 2,
        });
        break;
      case "centerX":
        layer.set({
          left: (referenceBoundingRect.left + referenceBoundingRect.right) / 2,
        });
        break;
    }
    layer.setCoords();
  }

  // Regroup the layers if they were previously in an ActiveSelection
  if (isActiveSelection && layers.length > 1) {
    const activeSelection = new ActiveSelection(layers, {
      canvas: fabricCanvas,
    });
    fabricCanvas.setActiveObject(activeSelection);
  }

  fabricCanvas.renderAll();
};
